import "./campaigns.css";

import {
  EXPERIMENT_STATUS,
  MISSION_MONITORING_STATIC_STATUS,
  PLOTMAP_STATUS,
} from "../constants";
import React, { useEffect, useMemo, useState } from "react";
import { getStatusColor, humanize, occurrences } from "../utils";
import { hasFeaturesRole, hasMonitoringRole } from "../users/rolesUtil";
import {
  requestFetchClientSite,
  requestUploadSummaryBySite,
} from "../services/backendRequests";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import BulkExportDrawer from "./bulkExportDrawer";
import { Button } from "reactstrap";
import { CountryFlag } from "../components/CountryFlag/CountryFlag";
import { DistributionDonut } from "./components/DistributionDonut";
import { LevelHeader } from "./components/LevelHeader";
import LoadingImg from "../components/loading";
import { MissionsPanel } from "./components/MissionsPanel";
import { RecapPanel } from "./components/RecapPanel";
import { SiteMonitoringPanel } from "./components/SiteMonitoringPanel";
import { UploadCounter } from "./components/UploadCounter";
import { UploadSessionsPanel } from "./components/UploadSessionsPanel";
import { fetchUserContractsTrials } from "../actions/user";
import { iso31661 } from "iso-3166";
import { loadMissionsMonitoring } from "../actions/missionMonitoring";

const EMPTY_MISSIONS = [];

const countryName = (alpha) =>
  iso31661.find((c) => c.alpha3 === alpha)?.name ?? "Unknown";

// Level 3: a site's recap (with parent campaign context) + missions / uploads.
const SitePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { campaignId, siteId } = useParams();

  const contracts = useSelector((store) => store.user.contracts) ?? [];
  const trials = useSelector((store) => store.user.trials) ?? [];
  const user = useSelector((store) => store.user);

  const [site, setSite] = useState(null);
  const [mode, setMode] = useState("missions");
  const [userPickedTab, setUserPickedTab] = useState(false);
  // Dates preselected in the export drawer, null while it is closed.
  const [exportDates, setExportDates] = useState(null);
  const [uploads, setUploads] = useState(null);

  // Missions come from the shared redux cache the campaign page populated,
  // filtered to this site, so drilling in never triggers a second fetch.
  const allMissions =
    useSelector((store) => store.missionMonitoring.byCampaign[campaignId]) ??
    EMPTY_MISSIONS;
  const missionsLoading =
    useSelector((store) => store.missionMonitoring.loading[campaignId]) ??
    false;
  const missions = useMemo(
    () => allMissions.filter((mission) => mission.site?.id === siteId),
    [allMissions, siteId]
  );

  const campaign = contracts.find((contract) => contract.id === campaignId);
  const canMonitor = Boolean(campaign && hasMonitoringRole(campaign.roles));

  // Site detail (with missions) is always fetched fresh by id.
  useEffect(() => {
    let active = true;
    requestFetchClientSite({ id: siteId }, user)
      .then((fetched) => active && setSite(fetched))
      .catch(() => active && setSite(null));
    return () => {
      active = false;
    };
  }, [siteId, user]);

  // Upload-session counts for this site (one cheap grouped query for the campaign).
  useEffect(() => {
    requestUploadSummaryBySite(campaignId, user)
      .then((summary) => setUploads(summary[siteId] ?? { total: 0 }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, siteId]);

  // Campaign context (breadcrumb + recap) comes from the contracts list.
  useEffect(() => {
    if (contracts.length === 0) dispatch(fetchUserContractsTrials());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Load this campaign's monitoring once, served from the redux cache the
  // campaign page already populated, so drilling into a site never re-fetches.
  // The role check lives in the thunk.
  useEffect(() => {
    dispatch(loadMissionsMonitoring(campaignId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, contracts]);

  // Monitoring is the primary tab: land on it whenever the user has the right
  // (its panel handles its own loading / "no data"), otherwise on Missions,
  // unless the user has already picked a tab themselves.
  useEffect(() => {
    if (!userPickedTab) setMode(canMonitor ? "monitoring" : "missions");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canMonitor, userPickedTab]);

  const trial = trials.find((item) => item.id === siteId);
  const trialDates = [...(trial?.trial_dates || [])].map(String).sort();
  const canExport = Boolean(trial) && hasFeaturesRole(campaign?.roles);

  const path = [
    { label: "Campaigns", to: "/campaigns" },
    { label: campaign?.name ?? "Campaign", to: `/campaigns/${campaignId}` },
    { label: site?.display_name ?? "Site" },
  ];

  if (!site)
    return (
      <div className="page-content container-fluid cf-page">
        <LevelHeader path={path} />
        <LoadingImg visible />
      </div>
    );

  const experiment = EXPERIMENT_STATUS.properties[site.experiment_status];
  const plotmap =
    PLOTMAP_STATUS.properties[
      site.plotmap_status ?? PLOTMAP_STATUS.NOT_DELIVERED
    ];
  const showUploads = Boolean(campaign?.upload_template);

  const siteItems = [
    {
      label: "Country",
      value: site.country ? (
        <>
          <CountryFlag alpha={site.country} /> {countryName(site.country)}
        </>
      ) : (
        "-"
      ),
    },
    { label: "Crop", value: site.crop || "-" },
    experiment && {
      label: "Field map",
      value: (
        <span className={`badge ${experiment.color}`}>{experiment.label}</span>
      ),
    },
    plotmap && {
      label: "Plot map",
      value: (
        <span style={{ color: plotmap.color, fontWeight: 650 }}>
          <i className={`${plotmap.icon} me-1`} />
          {plotmap.label}
        </span>
      ),
    },
    { label: "Missions", value: site.missions?.length ?? 0 },
    uploads && {
      label: "Upload sessions",
      value: <UploadCounter counts={uploads} />,
    },
  ].filter(Boolean);

  // Status distribution of this site's missions, fed to the Overview donut,
  // same segments shape the monitoring page uses.
  const statusList = [
    ...new Set([
      ...MISSION_MONITORING_STATIC_STATUS,
      ...missions.map((mission) => mission.status),
    ]),
  ];
  const statusCounts = occurrences(statusList, missions, "status");
  const overviewSegments = statusList
    .map((status) => ({
      label: humanize(status),
      value: statusCounts[status] ?? 0,
      color: getStatusColor(statusList, status, [], undefined),
      filterValue: status,
    }))
    .filter((segment) => segment.value > 0);

  return (
    <div className="page-content container-fluid cf-page">
      <LevelHeader
        path={path}
        actions={
          (showUploads || trial) && (
            <div className="d-flex gap-2">
              {showUploads && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() =>
                    history.push(
                      `/upload?contractId=${campaign.id}&siteId=${site.id}`
                    )
                  }
                >
                  <i className="fa-solid fa-cloud-arrow-up me-1" /> New upload
                  session
                </Button>
              )}
              {trial && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => history.push(`/map/${site.id}`)}
                >
                  <i className="fa-solid fa-earth-europe me-1" /> View results
                </Button>
              )}
              {canExport && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setExportDates(trialDates)}
                >
                  <i className="fa-solid fa-download me-1" /> Download results
                </Button>
              )}
            </div>
          )
        }
      />

      {/* Single-site mode, every date preselected from the header, the
          mission's date from a mission row. No plotIds: no map filter here,
          so the drawer hides the profile choice. */}
      {exportDates && (
        <BulkExportDrawer
          isOpen
          onClose={() => setExportDates(null)}
          user={user}
          filterContext={{
            // From the trial, not the campaign: coming from the map, the
            // trials are in the store before the contracts.
            contractInfoId: trial.contract_id,
            site: { id: site.id, display_name: site.display_name },
            dates: exportDates,
            availableDates: trialDates,
          }}
        />
      )}

      <div className="cf-split">
        <aside className="cf-split__aside">
          {/* Site detail */}
          <RecapPanel
            header={site.display_name}
            icon="fa-solid fa-location-dot"
            items={siteItems}
          />
          {/* Site acquisition distribution, same donut as the monitoring page */}
          {canMonitor && overviewSegments.length > 0 && (
            <DistributionDonut
              title="Overview"
              unit="dates"
              segments={overviewSegments}
            />
          )}
        </aside>

        <div className="cf-split__main">
          {(canMonitor || showUploads) && (
            <div className="cf-tabs">
              {canMonitor && (
                <button
                  className={mode === "monitoring" ? "is-active" : ""}
                  onClick={() => {
                    setUserPickedTab(true);
                    setMode("monitoring");
                  }}
                >
                  Site monitoring
                </button>
              )}
              <button
                className={mode === "missions" ? "is-active" : ""}
                onClick={() => {
                  setUserPickedTab(true);
                  setMode("missions");
                }}
              >
                Missions
              </button>
              {showUploads && (
                <button
                  className={mode === "upload" ? "is-active" : ""}
                  onClick={() => {
                    setUserPickedTab(true);
                    setMode("upload");
                  }}
                >
                  Upload sessions
                </button>
              )}
            </div>
          )}

          {mode === "monitoring" && canMonitor ? (
            <SiteMonitoringPanel
              missions={missions}
              loading={missionsLoading}
              user={user}
              siteId={siteId}
              refresh={() =>
                dispatch(
                  loadMissionsMonitoring(campaignId, {
                    force: true,
                    silent: true,
                  })
                )
              }
            />
          ) : mode === "upload" && showUploads ? (
            <UploadSessionsPanel site={site} contract={campaign} />
          ) : (
            <MissionsPanel
              site={site}
              missions={site.missions}
              onDownload={canExport ? (date) => setExportDates([date]) : null}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SitePage;
